import { format, parseISO, isValid, formatISO, startOfMonth, endOfMonth, formatDistanceToNow } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

/**
 * Formats a date string into "dd MMM yyyy" format (e.g., "05 Jan 2024").
 *
 * @param {string | null | undefined} dateStr - The input date string (ISO format recommended).
 * @returns {string} Formatted date string, or "—" if input is invalid or empty.
 */
export const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const datePart = typeof dateStr === "string" ? dateStr.split("T")[0] : dateStr;
  const parsedDate = parseISO(datePart);

  if (!isValid(parsedDate)) return "—";

  return format(parsedDate, "dd MMM yyyy");
};

/**
 * Returns a human-readable relative time string (e.g., "Today", "1 day ago", "5 days ago")
 * based on the difference between the current date and the given date.
 *
 * @param {string | Date | null | undefined} dateString - The input date (ISO string or Date object).
 * @returns {string | null} Relative day string, or null if input is missing.
 */
export const getDaysAgo = (dateString) => {
  if (!dateString) return null;

  const diffMs = Date.now() - new Date(dateString).getTime();

  // Prevent negative days due to timezone/server mismatch
  const diffDays = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "1 day ago";

  return `${diffDays} days ago`;
};

/**
 * Returns a short human-readable relative time string
 * (e.g., "just now", "5m ago", "2h ago", "3d ago")
 *
 * @param {string | Date | null | undefined} dateStr
 * @returns {string}
 */
export const timeAgo = (dateStr) => {
  if (!dateStr) return "now";

  const date = new Date(dateStr);

  if (isNaN(date.getTime())) return "now";

  const diffInSeconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return "just now";

  const minutes = Math.floor(diffInSeconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

/**
 * The browser's IANA timezone (e.g. "Asia/Kolkata"), or "UTC" when the
 * browser does not report one.
 *
 * @returns {string}
 */
export const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

/**
 * Formats a given date in the user's local timezone.
 *
 * @param {Date | string | number} date - The date to format. Can be a Date object, ISO string, or timestamp.
 * @param {string} [dateFormat='yyyy-MM-dd HH:mm:ss'] - The desired output format using date-fns tokens.
 * @returns {string} The formatted date string adjusted to the user's timezone.
 */
export const formatDateWithUserTimeZone = (date, dateFormat = "yyyy-MM-dd'T'HH:mm:ssxxx") => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Convert the date to the user's timezone
  const zonedDate = formatInTimeZone(new Date(date), userTimeZone, dateFormat);

  return zonedDate;
};

/**
 * The one relative clock in the app: "about 2 hours ago", "3 days ago".
 *
 * The server sends UTC with an offset on it, so `new Date` already places the
 * instant correctly and the browser's own zone does the rest. Nothing here
 * converts a timezone, and that is the point — the two callers before this
 * function existed each wrapped the value in `toZonedTime(value, <the
 * browser's own zone>)`, which is an identity on every machine that will ever
 * run it, and reads as if a correction were being applied.
 *
 * A missing value returns the em dash rather than a time. `new Date(null)` is
 * the 1970 epoch, so the unguarded version rendered "over 56 years ago" for a
 * row whose timestamp had not come back; `new Date(undefined)` is an Invalid
 * Date, and `formatDistanceToNow` throws a RangeError on one, which took the
 * whole tab down.
 *
 * @param {string | Date | null | undefined} value  an ISO instant
 * @returns {string} e.g. "about 2 hours ago", or "—"
 */
export const formatRelativeTime = (value) => {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (!isValid(date)) return "—";
  return formatDistanceToNow(date, { addSuffix: true });
};

/**
 * The exact local instant behind a relative time — "20 Aug 2026, 14:14".
 *
 * A relative time is easier to read and impossible to check, so it goes on
 * screen and this goes in the `title` beside it. Same format everywhere, in the
 * reader's own zone.
 *
 * @param {string | Date | null | undefined} value  an ISO instant
 * @returns {string} the formatted instant, or "" when there is nothing to show
 */
export const formatExactDateTime = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (!isValid(date)) return "";
  return format(date, "d MMM yyyy, HH:mm");
};

export const currentMonthStart = formatISO(startOfMonth(new Date()));
export const currentMonthEnd = formatISO(endOfMonth(new Date()));
